import React, { useEffect, useState } from 'react';
import { X, CheckCircle, WarningCircle, XCircle, Info, CircleNotch } from '@phosphor-icons/react';
import './DialogModal.css';

/**
 * DialogModal Component
 * Reusable custom dialog for replacing browser alerts/confirms/prompts.
 *
 * Props:
 *   isOpen: boolean - Whether dialog is visible
 *   onClose: function - Called when dialog is closed
 *   title: string - Dialog title
 *   message: string - Dialog message/content
 *   type: 'info' | 'success' | 'warning' | 'error' - Dialog type
 *   primaryAction: object - { label, onClick } — onClick receives the
 *     entered text as its only argument when `input` is set (replaces
 *     window.prompt); called with no arguments otherwise.
 *   secondaryAction: object - { label, onClick } (optional) — omit
 *     entirely for a single-button "alert" dialog (replaces window.alert);
 *     include it for a two-button "confirm" dialog (replaces window.confirm).
 *   showCloseButton: boolean - Show X button (default: true)
 *   isLoading: boolean - Show loading state (default: false)
 *   disableCloseWhenLoading: boolean - Prevent closing/interactions during loading (default: true)
 *   input: object (optional) - { label, placeholder, defaultValue, required, multiline } —
 *     renders a text field and turns this into a prompt-style dialog
 *     (replaces window.prompt). primaryAction.onClick(value) receives the
 *     current field value; if required, the primary button stays disabled
 *     until non-empty.
 */
function DialogModal({
  isOpen,
  onClose,
  title,
  message,
  type = 'info',
  primaryAction,
  secondaryAction,
  showCloseButton = true,
  isLoading = false,
  disableCloseWhenLoading = true,
  input,
}) {
  const [inputValue, setInputValue] = useState(input?.defaultValue || '');

  useEffect(() => {
    if (isOpen) setInputValue(input?.defaultValue || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const inputBlocksSubmit = !!(input?.required && !inputValue.trim());

  // Icon mapping based on type
  const iconMap = {
    info: <Info size={32} weight="fill" className="dialog-icon dialog-icon-info" />,
    success: <CheckCircle size={32} weight="fill" className="dialog-icon dialog-icon-success" />,
    warning: <WarningCircle size={32} weight="fill" className="dialog-icon dialog-icon-warning" />,
    error: <XCircle size={32} weight="fill" className="dialog-icon dialog-icon-error" />
  };

  // Get border color based on type
  const borderColorClass = `dialog-border-${type}`;

  return (
    <div className="dialog-overlay" onClick={disableCloseWhenLoading && isLoading ? undefined : onClose}>
      <div className={`dialog-modal ${borderColorClass}`} onClick={(e) => e.stopPropagation()}>
        {/* Close Button */}
        {showCloseButton && (
          <button 
            className="dialog-close-btn" 
            onClick={onClose}
            disabled={disableCloseWhenLoading && isLoading}
            aria-label="Close"
          >
            <X size={20} weight="bold" />
          </button>
        )}

        {/* Icon */}
        <div className="dialog-icon-container">
          {isLoading ? (
            <CircleNotch size={32} className="dialog-icon-loading" weight="fill" />
          ) : (
            iconMap[type]
          )}
        </div>

        {/* Content */}
        <div className="dialog-content">
          {title && <h2 className="dialog-title">{title}</h2>}
          {message && <p className="dialog-message">{message}</p>}
          {input && (
            <div className="dialog-input-group">
              {input.label && <label className="dialog-input-label">{input.label}</label>}
              {input.multiline ? (
                <textarea
                  className="dialog-input"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder={input.placeholder}
                  rows={3}
                  autoFocus
                />
              ) : (
                <input
                  type="text"
                  className="dialog-input"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder={input.placeholder}
                  autoFocus
                />
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="dialog-actions">
          {secondaryAction && (
            <button
              className="btn btn-secondary"
              onClick={() => {
                if (secondaryAction.onClick) secondaryAction.onClick();
                onClose();
              }}
              disabled={disableCloseWhenLoading && isLoading}
            >
              {secondaryAction.label}
            </button>
          )}
          {primaryAction && (
            <button
              className={`btn btn-${type}`}
              onClick={() => {
                if (primaryAction.onClick) primaryAction.onClick(input ? inputValue : undefined);
                onClose();
              }}
              disabled={(disableCloseWhenLoading && isLoading) || inputBlocksSubmit}
            >
              {isLoading ? (
                <span className="button-loading">
                  <CircleNotch size={16} weight="fill" className="spinner" />
                  {primaryAction.loadingLabel || 'Loading...'}
                </span>
              ) : (
                primaryAction.label
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default DialogModal;
